import axios, { AxiosInstance } from "axios";
import { toast } from "sonner";
import {
  getAccessToken,
  clearAllTokens,
  saveAccessToken,
  saveRefreshToken,
} from "../utils/storage.utils";
import { queryClient } from "../providers/AppProviders";
import { refreshToken } from "../services/auth.service";

// Create axios instance with base configuration
const axiosInstance: AxiosInstance = axios.create({
  baseURL: process.env.API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: any[] = [];

const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token);
    } else {
      prom.reject(error);
    }
  });

  failedQueue = [];
};

// Request interceptor to add authorization header
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get token from Office.js settings
    // Do not add Authorization header for sign-in endpoint
    if (config.url?.endsWith("auth/sign-in") || config.url?.endsWith("auth/refresh")) {
      return config;
    }

    const token = await getAccessToken();

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    // Handle common error scenarios
    const originalRequest = error.config;

    const status = error.response?.status;
    const url = originalRequest.url;
    const detail = error.response?.data?.detail;

    // Check for 402 Payment Required with "Not enough balance" message
    if (
      [402].includes(status) &&
      typeof detail === "string" &&
      detail.includes("Not enough balance")
    ) {
      const { topUpEvents, TOP_UP_REQUIRED } = await import("../utils/events");
      topUpEvents.emit(TOP_UP_REQUIRED);
    }

    let shouldLogout = false;

    if (status === 401 || status === 403) {
      if (!url?.includes("auth/sign-in") && !url?.includes("auth/refresh")) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        isRefreshing = true;

        try {
          const { access_token, refresh_token } = await refreshToken();
          saveAccessToken(access_token);
          saveRefreshToken(refresh_token);
          isRefreshing = false;
          processQueue(null, access_token);
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError);
          shouldLogout = true;
        } finally {
          isRefreshing = false;
        }
      } else {
        shouldLogout = true;
      }
    }

    if (shouldLogout) {
      // Handle unauthorized/forbidden access - clear all tokens and trigger logout
      await clearAllTokens();

      if (status === 403) {
        toast.error("You don't have permission to access this document.");
      }

      queryClient.setQueryData(["user"], null);
      queryClient.removeQueries();
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
